if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          progressDots: this.querySelectorAll('[data-gallery-dot-index]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isPremiumProductGallery = this.closest('.product-page--premium') !== null;
        this.hoverAutoplayDelay = 2200;
        this.hoverAutoplayId = null;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.isPremiumProductGallery) {
          this.elements.viewer.addEventListener('mouseenter', this.startHoverAutoplay.bind(this));
          this.elements.viewer.addEventListener('mouseleave', this.stopHoverAutoplay.bind(this));
        }
        this.elements.progressDots.forEach((dot) => {
          dot.addEventListener('click', this.setActiveDotMedia.bind(this, Number(dot.dataset.galleryDotIndex)));
        });
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          if (this.isPremiumProductGallery) {
            button.addEventListener('mouseenter', this.previewMedia.bind(this, mediaToSwitch.dataset.target));
            button.addEventListener('focus', this.previewMedia.bind(this, mediaToSwitch.dataset.target));
          }
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.setActiveProgressDot(event.detail.currentPage - 1);
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveDotMedia(index, event) {
        event?.preventDefault();

        const slide = this.elements.viewer.sliderItemsToShow?.[index];
        if (!slide) return;

        this.elements.viewer.setSlidePosition(slide.offsetLeft);
        this.setActiveProgressDot(index);
        if (slide.dataset.mediaId) {
          this.setActiveMedia(slide.dataset.mediaId, false);
        }
      }

      setActiveProgressDot(index) {
        if (!this.elements.progressDots.length) return;

        this.elements.progressDots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === index;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      previewMedia(mediaId) {
        if (!this.mql.matches) return;
        if (!this.dataset.desktopLayout.includes('thumbnail')) return;
        this.setActiveMedia(mediaId, false);
      }

      startHoverAutoplay() {
        if (!this.isPremiumProductGallery) return;
        if (!this.mql.matches) return;
        if (this.hoverAutoplayId) return;

        this.hoverAutoplayId = window.setInterval(() => {
          this.advanceToNextMedia();
        }, this.hoverAutoplayDelay);
      }

      stopHoverAutoplay() {
        if (!this.hoverAutoplayId) return;
        window.clearInterval(this.hoverAutoplayId);
        this.hoverAutoplayId = null;
      }

      advanceToNextMedia() {
        if (!this.elements.thumbnails) return;

        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        if (thumbnailItems.length < 2) return;

        const activeIndex = thumbnailItems.findIndex((item) =>
          item.querySelector('button[aria-current="true"]')
        );
        const nextIndex = activeIndex >= 0 ? (activeIndex + 1) % thumbnailItems.length : 0;
        const nextItem = thumbnailItems[nextIndex];
        if (!nextItem || !nextItem.dataset.target) return;

        this.setActiveMedia(nextItem.dataset.target, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
